import type {
  FieldMergeFunction,
  FieldPolicy,
  FieldReadFunction,
  TypePolicies,
} from '@apollo/client'
import { Reference, relayStylePagination } from '@apollo/client/utilities'
import type { TExistingRelay, TRelayPageInfo } from '@apollo/client/utilities/policies/pagination'
import { RESOURCE_REGISTRY_PAGINATION_INFO } from 'generated/resources/namespaces'

type TRelayEdge<TNode> = {
  cursor?: string
  node: TNode
}

const PAGINATION_KEYS = ['first', 'offsetPage', 'last', 'before', 'after']

const apolloCursorMergePolicy = relayStylePagination().merge as FieldMergeFunction
const apolloCursorReadPolicy = relayStylePagination().read as FieldReadFunction

function makeEmptyData(): TExistingRelay<unknown> {
  return {
    edges: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: true,
      startCursor: '',
      endCursor: '',
    },
  }
}

const offsetReadPolicy: FieldReadFunction = (existing, { args, variables }) => {
  if (existing.edges && args) {
    const paginationInfiniteScroll = variables?.['__paginationInfiniteScroll']
    const { offsetPage = 1, first = 10 } = args

    let edges: TRelayEdge<Reference>[] = []
    if (paginationInfiniteScroll) {
      edges = existing.edges.slice(0, first * offsetPage)
    } else {
      const edgesStart = (offsetPage - 1) * first
      const edgesEnd = edgesStart + first

      edges = existing.edges.slice(edgesStart, edgesEnd)
    }

    return {
      ...existing,
      edges,
    }
  }

  // eslint-disable-next-line no-console
  console.error('Existing edges or args are not defined', existing)
  return existing
}

const truncateCachedEdgesToTotalCount = (
  edges: Array<unknown> | null | undefined,
  totalCount: number | null | undefined
) => {
  if (!edges || totalCount === 0) {
    return []
  }

  return totalCount ? edges.slice(0, totalCount) : [...edges]
}

// Custom implementation of
// https://github.com/apollographql/apollo-client/blob/main/src/utilities/policies/pagination.ts#L28-L55
const offsetMergePolicy: FieldMergeFunction = (existing, incoming, { args, mergeObjects }) => {
  if (incoming?.edges) {
    const edges = truncateCachedEdgesToTotalCount(existing?.edges, incoming?.totalCount)

    const { offsetPage = 1, first = 10 } = args ?? {}
    const firstElelementInPage = (offsetPage - 1) * first

    for (let i = 0; i < incoming.edges.length; ++i) {
      edges[firstElelementInPage + i] = incoming.edges[i]
    }

    return {
      ...incoming,
      edges,
    }
  }

  // eslint-disable-next-line no-console
  console.error('Incoming edges are not defined', incoming)
  return mergeObjects(existing, incoming)
}

const cursorReadPolicy: FieldReadFunction = (existing, options) => {
  if (!existing) return existing

  // If existing doesn't have edges array, return as-is to avoid errors
  if (!existing.edges || !Array.isArray(existing.edges)) {
    return existing
  }

  const edges: TRelayEdge<Reference>[] = existing.edges
  const { startCursor, endCursor }: TRelayPageInfo = existing?.pageInfo || {}

  const startNodeIndex = edges.findIndex((edge) => edge.cursor === startCursor)
  const endNodeIndex = edges.findIndex((edge) => edge.cursor === endCursor)

  const newEdges = edges.slice(startNodeIndex, endNodeIndex + 1) ?? []

  return {
    ...existing,
    edges: newEdges,
  }
}

// Custom implementation of
// https://github.com/apollographql/apollo-client/blob/main/src/utilities/policies/pagination.ts#L140-L268
const cursorMergePolicy: FieldMergeFunction = (existing, incoming, { args, fieldName }) => {
  if (!existing) {
    existing = makeEmptyData()
  }

  const incomingEdges = incoming.edges ? [...incoming.edges] : []
  let prefix: TRelayEdge<Reference>[] = existing.edges || []
  let suffix: TRelayEdge<Reference>[] = []

  if (args && args.after) {
    const index = prefix.findIndex((edge) => edge.cursor === args.after)
    if (index >= 0) {
      prefix = prefix.slice(0, index + 1)
    }
  } else if (args && args.before) {
    const index = prefix.findIndex((edge) => edge.cursor === args.before)
    suffix = index < 0 ? prefix : prefix.slice(index)
    prefix = []
  } else if (incoming.edges) {
    prefix = []
  }

  const edges = [...prefix, ...incomingEdges, ...suffix]

  const pageInfo: TRelayPageInfo = incoming.pageInfo || {}

  return {
    ...existing,
    ...incoming,
    edges,
    pageInfo,
  }
}

export default function buildPaginationPolicy(): TypePolicies {
  return RESOURCE_REGISTRY_PAGINATION_INFO.reduce<Record<string, { fields: TypePolicies }>>(
    (acc, current) => {
      const { typeName, policyTypeName } = current

      const policy: FieldPolicy = {
        keyArgs: (args) => {
          if (!args) return false

          if (
            '__notExcludePaginationKeysFromKeyArgs' in args &&
            args.__notExcludePaginationKeysFromKeyArgs
          ) {
            return Object.keys(args)
          }

          return Object.keys(args).filter((value) => !PAGINATION_KEYS.includes(value))
        },
        read(existing, options) {
          if (!existing) return existing

          const { variables } = options

          const paginationKind = variables?.['__paginationKind']
          const paginationInfiniteScroll = variables?.['__paginationInfiniteScroll']
          const isPagination = paginationKind !== undefined

          if (!isPagination) return existing

          if (paginationKind === 'offset') {
            return offsetReadPolicy(existing, options)
          }
          if (paginationKind === 'cursor') {
            if (paginationInfiniteScroll) {
              // Apollo's relayStylePagination expects existing to have edges array
              // If existing is malformed (exists but missing edges), return as-is to let Apollo handle it
              // This avoids creating artificial empty data during initial load
              if (existing && (!existing.edges || !Array.isArray(existing.edges))) {
                return existing
              }
              return apolloCursorReadPolicy(existing, options)
            }
            return cursorReadPolicy(existing, options)
          }

          throw new Error(`Unknown pagination kind: ${paginationKind}`)
        },
        merge(existing, incoming, options) {
          const { mergeObjects, variables } = options

          const paginationKind = variables?.['__paginationKind']
          const isPagination = paginationKind !== undefined

          if (!isPagination) return mergeObjects(existing, incoming)

          if (paginationKind === 'offset') {
            return offsetMergePolicy(existing, incoming, options)
          }

          if (paginationKind === 'cursor') {
            const paginationInfiniteScroll = variables?.['__paginationInfiniteScroll']

            if (paginationInfiniteScroll) {
              // If incoming doesn't have edges (partial query like { totalCount }), use simple merge
              if (!incoming?.edges) {
                return mergeObjects(existing, incoming)
              }

              // Apollo's relayStylePagination expects existing to have a specific structure
              // Initialize it if undefined to prevent "Cannot read properties of undefined (reading 'length')" error
              const safeExisting = existing || makeEmptyData()
              return apolloCursorMergePolicy(safeExisting, incoming, options)
            }

            return cursorMergePolicy(existing, incoming, options)
          }

          throw new Error(`Unknown pagination kind: ${paginationKind}`)
        },
      }

      return {
        ...acc,
        [typeName]: {
          fields: {
            ...(acc[typeName]?.fields ?? {}),
            [policyTypeName]: policy,
          },
        },
      }
    },
    {}
  )
}
